import { useEffect, useState, useRef } from "react";
import {
  Activity,
  TrendingUp,
  AlertCircle,
  Wallet,
  DollarSign,
  Target,
  ShoppingBag,
  Coins,
  Receipt,
  Scale,
  ArrowUpRight,
  ArrowDownRight,
  Percent,
  Trophy,
  Medal,
  ChevronRight,
  Store,
  X,
} from "lucide-react";
import {AnimatePresence, motion } from "framer-motion";
import {
  PieChart,
  Pie,
  LineChart,
  Line,
  Cell,
  XAxis,
  YAxis,
  ComposedChart,
  Tooltip,
  ResponsiveContainer,
  Legend,
  BarChart,
  Bar,
  CartesianGrid,
  ReferenceLine
} from "recharts";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { apiFetch } from "../utils/apiFetch";


import PageWrapper from "../components/PageWrapper";

export default function MarketingHq() {
  const chartRef = useRef(null);
  const [data, setData] = useState(null);
  const [selectedStore, setSelectedStore] = useState(null);
  const [activeProgram, setActiveProgram] = useState(null);
  const [yoyData, setYoyData] = useState([]);
  const [activeBrand, setActiveBrand] = useState(null);
  const [selectedSpi, setSelectedSpi] = useState(null);
  const [spiData, setSpiData] = useState([]);
  const [activeDept, setActiveDept] = useState(null);
  const [selectedContent, setSelectedContent] = useState(null);
  const [deptDataApi, setDeptDataApi] = useState([]);
  const [programData, setProgramData] = useState([]);
  const [brandDataApi, setBrandDataApi] = useState([]);
  const [popularProducts, setPopularProducts] = useState([]);
  const [contentPerformanceData, setContentPerformanceData] = useState([]);

  // Pilihan periode dinamis
  const [currentMonth, setCurrentMonth] = useState("6");    
  const [compareMonth, setCompareMonth] = useState("5");    
  const [lastYearMonth, setLastYearMonth] = useState("6");  
  const [activeContentTab, setActiveContentTab] = useState("ALL");
  const [marketingRawData, setMarketingRawData] = useState([]);

  const [summary, setSummary] = useState(null);
  const [activeLine, setActiveLine] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [chartData, setChartData] = useState([]);
  const [selectedProgram, setSelectedProgram] = useState(null);
  const [storeGrowthData, setStoreGrowthData] = useState([]);

  useEffect(() => {
    if (programData.length > 0 && !selectedProgram) {
      setSelectedProgram(programData[0]);
    }
  }, [programData]);

  const formatRupiah = (num) =>
    new Intl.NumberFormat("id-ID", {
      style: "currency",
      currency: "IDR",
      maximumFractionDigits: 0,
    }).format(num || 0);

  const [chartSize, setChartSize] = useState({ w: 0, h: 0 });

  useEffect(() => {
    if (chartRef.current) {
      const { offsetWidth, offsetHeight } = chartRef.current;
      setChartSize({ w: offsetWidth, h: offsetHeight });
    }
  }, [programData]);

  const getMonthFormat = (m, yearOffset = 0) => {
    if (m === "ALL") return "ALL";
    const year = new Date().getFullYear() - yearOffset;
    return `${year}-${String(m).padStart(2, "0")}-01`; 
  };

  const totalRevenue = programData.reduce((acc, cur) => acc + cur.revenue, 0);
  const RADIAN = Math.PI / 180;

  const renderNeedle = (value, total, cx, cy, iR, oR, color) => {
    const angle = 180 * (1 - value / total);
    const length = (iR + 2 * oR) / 3;
    const sin = Math.sin(-RADIAN * angle);
    const cos = Math.sin(-RADIAN * angle);
    const x0 = cx;
    const y0 = cy;
    const x1 = x0 + length * cos;
    const y1 = y0 + length * sin;

    return (
      <>
        <circle cx={x0} cy={y0} r={5} fill={color} />
        <path d={`M${x0},${y0} L${x1},${y1}`} stroke={color} strokeWidth={3} />
      </>
    );
  };

  const needleValue = selectedProgram?.revenue || 0;
  
  const handleStoreClick = async (store) => {
    setSelectedStore({
      ...store,
      programContributions: [], 
      isLoadingContributions: true
    });

    try {
      const bulanParam = typeof currentMonth !== 'undefined' ? getMonthFormat(currentMonth, 0) : 'ALL';
      const response = await fetch(`/api/sales/store-program-contribution?kode_toko=${store.storeCode}&bulan=${bulanParam}`);
      const result = await response.json();

      if (result.success) {
        setSelectedStore(prev => {
          if (!prev || prev.storeCode !== store.storeCode) return prev;
          return {
            ...prev,
            programContributions: result.data,
            isLoadingContributions: false
          };
        });
      } else {
        console.error("Backend Error:", result.message);
        setSelectedStore(prev => ({ ...prev, isLoadingContributions: false }));
      }
    } catch (error) {
      console.error("Gagal memuat data kontribusi program:", error);
      setSelectedStore(prev => ({ ...prev, isLoadingContributions: false }));
    }
  };

  const getMonthLabel = (m, yearOffset = 0) => {
    if (m === "ALL") return "All_Months";
    const year = new Date().getFullYear() - yearOffset;
    const monthName = new Date(0, m - 1).toLocaleString("id-ID", {
      month: "long",
    });
    return `${monthName}_${year}`;
  };

 

  const exportToExcel = () => {
    const wb = XLSX.utils.book_new();
    const summarySheet = XLSX.utils.json_to_sheet([
      {
        Month: currentMonth,
        Target: summary?.target,
        "MTD Sales": summary?.mtd_sales,
        "Ach to Full Moon (%)": summary?.ach_to_full_moon,
        "GAP to Target": summary?.gap_to_target,
        "GAP vs Compare Month": summary?.gap_vs_compare_month,
        "GAP vs Last Year Month": summary?.gap_vs_last_year,
        "Transaction Total": summary?.transaction_total,
        AOV: summary?.aov,
        "Price Point": summary?.price_point,
        QTY: summary?.qty,
        "Basket Size": summary?.basket_size,
      },
    ]);

    XLSX.utils.book_append_sheet(wb, summarySheet, "Summary");
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(spiData), "SPI ");
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(brandDataApi), "Brand Contribution");
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(deptDataApi), "Dept Contribution");
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(yoyData), "YoY");
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(chartData), "Daily Traffic");
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(programData), "Sales by Program Acara");

    const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const file = new Blob([excelBuffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
    saveAs(file, `Dashboard_Comparison_${getMonthLabel(currentMonth, 0)}.xlsx`);
  };



  const selectedData = yoyData.find((item) => item.month === selectedMonth) || yoyData[yoyData.length - 1];

  

  const fetchBrand = async () => {
    try {
      const token = localStorage.getItem("token");
      const kode_toko = localStorage.getItem("kode_toko");
      const params = new URLSearchParams();
      params.append("bulan", getMonthFormat(currentMonth, 0));
      params.append("kode_toko", kode_toko);
      params.append("limit", 10);
      params.append("sort_by", "revenue");

      const res = await apiFetch(`/api/sales/summary-brand?${params.toString()}`, {
        headers: { "x-api-key": "SECRET123456", Authorization: `Bearer ${token}` },
      });
      const json = await res.json();
      setBrandDataApi((json.data || []).map(item => ({
        name: item.brand,
        value: Number(item.net_revenue || 0),
        qty: Number(item.total_qty || 0),
        trx: Number(item.total_transaksi || 0),
      })));
    } catch (err) { console.log("brand error:", err); }
  };

  const fetchYoY = async () => {
    try {
      const params = new URLSearchParams();
      const token = localStorage.getItem("token");
      const kode_toko = localStorage.getItem("kode_toko");
      params.append("bulan", getMonthFormat(currentMonth, 0));
      params.append("kode_toko", kode_toko);

      const res = await apiFetch(`/api/sales/chart-yoy-summary?${params.toString()}`, {
        headers: { "x-api-key": "SECRET123456", Authorization: `Bearer ${token}` },
      });
      const json = await res.json();
      setYoyData((json.data || []).map(item => ({
        month: item.month,
        thisYear: Number(item.gross_current),
        lastYear: Number(item.gross_last),
        netThis: Number(item.net_current),
        netLast: Number(item.net_last),
        trxThis: Number(item.trx_current),
        trxLast: Number(item.trx_last),
        growth: Number(item.growth_pct),       
        momGrowth: Number(item.mom_growth_pct) 
      })));
    } catch (err) { console.log("YoY error:", err); }
  };


  const fetchChart = async () => {
    try {
      const token = localStorage.getItem("token");
      const kode_toko = localStorage.getItem("kode_toko");
      const params = new URLSearchParams();
      params.append("bulan", getMonthFormat(currentMonth, 0));
      params.append("kode_toko", kode_toko);

      const res = await apiFetch(`/api/sales/chart-overview?${params.toString()}`, {
        headers: { "x-api-key": "SECRET123456", Authorization: `Bearer ${token}` },
      });
      const json = await res.json();
      
      setChartData((json.data || []).map(item => ({
        date: item.date, 
        revenue: Number(item.gross_revenue) || 0,
        net_revenue: Number(item.net_revenue) || 0,                 
        daily_target: Number(item.daily_target) || 0,               
        achievement_percent: Number(item.achievement_percent) || 0, 
        transactions: Number(item.total_transaksi) || 0,
      })));
    } catch (err) { 
      console.log("chart error:", err); 
    }
  };

  // 🔥 SINKRONISASI & PERBAIKAN LOGIKA REDUCE DI SINI
  const fetchSummary = async () => {
    try {
      const token = localStorage.getItem("token");
      const kode_toko = localStorage.getItem("kode_toko");
      const url = `/api/marketing/summary-monthly?current_bulan=${getMonthFormat(currentMonth, 0)}&compare_bulan=${getMonthFormat(compareMonth, 0)}&last_year_bulan=${getMonthFormat(lastYearMonth, 1)}&kode_toko=${kode_toko}`;

      const res = await apiFetch(url, {
        headers: {
          "x-api-key": "SECRET123456",
          Authorization: `Bearer ${token}`,
        },
      }).then((r) => r.json());

      if (res.success && res.data) {
        // Tampung data array response API Anda
        const marketingData = res.data || [];
        setMarketingRawData(marketingData);

         const contentPerf = res.contentPerformance || res.data.contentPerformance || [];
        setContentPerformanceData(contentPerf);
        // Akumulasikan semua item data marketing
       const summaryMarketing = marketingData.reduce((acc, curr) => {
  acc.total_budget += Number(curr.current_budget || 0);
  acc.total_realisasi_budget += Number(curr.current_realisasi_budget || 0);
  acc.total_target_views += Number(curr.current_target_views || 0);
  acc.total_views += Number(curr.current_views || 0);
  acc.total_likes += Number(curr.current_like_count || 0);
  acc.total_shares += Number(curr.current_share || 0);
  acc.compare_realisasi_budget += Number(curr.compare_realisasi_budget || 0);
  acc.sum_mom_views_pct += Number(curr.mom_views_percent || 0);
  acc.mom_budget_diff += Number(curr.mom_budget_diff || 0);
  acc.compare_views += Number(curr.compare_views || 0);
  acc.mom_views_diff+=Number(curr.mom_views_diff || 0);

   acc.compare_budget += Number(curr.compare_budget || 0);
  
  // 🔥 LOGIKA GROUPING UNTUK MASING-MASING FASE
  const faseClean = curr.fase ? curr.fase.toString().trim().toUpperCase() : "";
  
  if (faseClean === "FASE 1") {
    acc.total_views_fase_1 += Number(curr.current_views || 0);
    acc.mom_views_diff_fase_1 += Number(curr.mom_views_diff || 0);
    acc.compare_views_fase_1 += Number(curr.compare_views || 0);
  } else if (faseClean === "FASE 2") {
    acc.total_views_fase_2 += Number(curr.current_views || 0);
     acc.mom_views_diff_fase_2 += Number(curr.mom_views_diff || 0);
     acc.compare_views_fase_2 += Number(curr.compare_views || 0);
  } else if (faseClean === "FASE 3") {
    acc.total_views_fase_3 += Number(curr.current_views || 0);
     acc.mom_views_diff_fase_3 += Number(curr.mom_views_diff || 0);
     acc.compare_views_fase_3 += Number(curr.compare_views || 0);
  }

  return acc;
}, {
  total_budget: 0,
  total_realisasi_budget: 0,
  total_target_views: 0,
  total_views: 0,
  total_likes: 0,
  total_shares: 0,
  compare_realisasi_budget: 0,
  sum_mom_views_pct: 0,
  // 🔥 INITIAL VALUE UNTUK FASE 1, 2, & 3
  total_views_fase_1: 0,
  total_views_fase_2: 0,
  total_views_fase_3: 0,
  compare_budget:0,
  mom_views_diff:0,
  compare_views:0,

  mom_budget_diff:0,
  compare_views_fase_1:0,
  compare_views_fase_2:0,
  compare_views_fase_3:0,
  mom_views_diff_fase_1:0,
  mom_views_diff_fase_2:0,
  mom_views_diff_fase_3:0
});

        const totalItems = marketingData.length || 1;

        // Petakan hasil reduce kembali ke skema penamaan objek state 'summary' Anda
        setSummary({
          // Mengisi value dasar properti visual stat Anda
          budget_plan: summaryMarketing.total_budget,
          total_spent: summaryMarketing.total_realisasi_budget,
          total_view: summaryMarketing.total_views,  
          engagement: summaryMarketing.total_likes+summaryMarketing.total_shares,  
          gap_to_target: summaryMarketing.total_realisasi_budget - summaryMarketing.total_budget,
          gap_vs_compare_month: summaryMarketing.total_realisasi_budget - summaryMarketing.total_budget,
          
          // Mengisi properti Views, Absurd (Likes), Semi Absurd (Shares), Validasi (Target Views)
          fase_1_views: summaryMarketing.total_views_fase_1, 
          fase_2_views: summaryMarketing.total_views_fase_2, 
          fase_3_views: summaryMarketing.total_views_fase_3, 

          compare_realisasi_budget:summaryMarketing.compare_realisasi_budget,

          compare_views:summaryMarketing.compare_views,
          mom_views_diff:summaryMarketing.mom_views_diff,
          mom_views_diff_fase_1:summaryMarketing.mom_views_diff_fase_1,
           mom_views_diff_fase_2:summaryMarketing.mom_views_diff_fase_2,
            mom_views_diff_fase_3:summaryMarketing.mom_views_diff_fase_3,

             compare_views_fase_1:summaryMarketing.compare_views_1,
             compare_views_fase_2:summaryMarketing.compare_views_2,
             compare_views_fase_3:summaryMarketing.compare_views_3,
          
          // Mengisi persentase pertumbuhan MoM & YoY
          mom_budget: 100-(summaryMarketing.compare_budget / summaryMarketing.total_budget * 100),
          mom_realisasi_budget: 100-(summaryMarketing.total_realisasi_budget / summaryMarketing.compare_realisasi_budget * 100),
         mom_view_prc: (Number(summaryMarketing?.compare_views) || 0) > 0 
  ? (( (Number(summaryMarketing?.mom_views_diff) || 0) ) / (Number(summaryMarketing?.compare_views) || 0)) * 100 
  : 0,

   mom_view_prc_fase_1: (Number(summaryMarketing?.compare_views_1) || 0) > 0 
  ? (( (Number(summaryMarketing?.mom_views_diff_fase_1) || 0) ) / (Number(summaryMarketing?.compare_views_1) || 0)) * 100 
  : 0,

   mom_view_prc_fase_2: (Number(summaryMarketing?.compare_views_2) || 0) > 0 
  ? (( (Number(summaryMarketing?.mom_views_diff_fase_2) || 0) ) / (Number(summaryMarketing?.compare_views_2) || 0)) * 100 
  : 0,


    mom_view_prc_fase_3: (Number(summaryMarketing?.compare_views_3) || 0) > 0 
  ? (( (Number(summaryMarketing?.mom_views_diff_fase_3) || 0) ) / (Number(summaryMarketing?.compare_views_3) || 0)) * 100 
  : 0,

         
          growth_sales_vs_last_year_pct: 0, // Set default jika backend belum menyuplai YoY
          
          // Gap Tambahan untuk indikator delta MoM kartu bawah
          gap_trx_vs_compare: 0,
          gap_aov_vs_compare: 0,
          gap_price_vs_compare: 0,
          gap_budget: summaryMarketing.mom_budget_diff,
          gap_basket_vs_compare: 0,
          ach_to_full_moon: (summaryMarketing.total_realisasi_budget / Math.max(summaryMarketing.total_budget, 1)) * 100
        });
      }
    } catch (err) {
      console.error("fetchSummary error:", err);
    }
  };

  useEffect(() => {
    fetchSummary();
    fetchChart();
    fetchYoY();
  }, [currentMonth, compareMonth, lastYearMonth]);

  const cx = chartSize.w * 0.5;
  const cy = chartSize.h * 0.55;

  const renderIndicator = (val, isPercent = false, customPrefix = "") => {
    if (val === undefined || val === null) return <span className="text-gray-400">—</span>;
    const num = Number(val);
    const isPositive = num >= 0;
    
    let formattedVal = "";
    if (isPercent) {
      formattedVal = `${Math.abs(num).toFixed(1)}%`;
    } else {
      formattedVal = Math.abs(num).toLocaleString("id-ID", { maximumFractionDigits: 0 });
    }

    return (
      <span className={`inline-flex items-center font-semibold gap-0.5 px-1.5 py-0.5 rounded-md ${
        isPositive ? "text-emerald-700 bg-emerald-50" : "text-rose-700 bg-rose-50"
      }`}>
        {isPositive ? <ArrowUpRight size={12} className="stroke-[2.5]" /> : <ArrowDownRight size={12} className="stroke-[2.5]" />}
        {isPositive ? "+" : "-"}{customPrefix}{formattedVal}
      </span>
    );
  };


  const renderIndicatorCpv = (val, isPercent = false, customPrefix = "") => {
    if (val === undefined || val === null) return <span className="text-gray-400">—</span>;
    const num = Number(val);
    const isPositive = num >= 0;
    
    let formattedVal = "";
    if (isPercent) {
      formattedVal = `${Math.abs(num).toFixed(1)}%`;
    } else {
      formattedVal = Math.abs(num).toLocaleString("id-ID", { maximumFractionDigits: 0 });
    }

    return (
      <span className={`inline-flex items-center font-semibold gap-0.5 px-1.5 py-0.5 rounded-md ${
        isPositive ? "text-rose-700 bg-rose-50" : "text-emerald-700 bg-emerald-50"
      }`}>
        {isPositive ? <ArrowUpRight size={12} className="stroke-[2.5]" /> : <ArrowDownRight size={12} className="stroke-[2.5]" />}
        {isPositive ? "+" : "-"}{customPrefix}{formattedVal}
      </span>
    );
  };

  // 1. Hitung CPV Bulan Ini
const currentSpent = Number(summary?.total_spent) || 0;
const currentView = Number(summary?.total_view) || 0;
const currentCPV = currentView > 0 ? (currentSpent / currentView) : 0;

// 2. Hitung CPV Bulan Pembanding
const compareSpent = Number(summary?.compare_realisasi_budget) || 0;
const compareView = Number(summary?.compare_views) || 0;
const compareCPV = compareView > 0 ? (compareSpent / compareView) : 0;

// 3. Hitung Persentase MoM CPV (Amankan dari pembagian dengan 0)
const momCpvPercent = compareCPV > 0 
  ? ((currentCPV - compareCPV) / compareCPV) * 100 
  : 0;

  const stats = [
    {
      label: "Budget Plan",
      value: summary ? formatRupiah(summary.budget_plan) : "Rp 0",
      icon: Target,
      color: "text-blue-600 bg-gradient-to-br from-blue-100 to-blue-50/50 border-blue-200 shadow-blue-50/50",
      mom: summary ? renderIndicator(summary.mom_budget, true) : "0%",
      //yoy: summary ? renderIndicator(summary.growth_sales_vs_last_year_pct, true) : "0%",
      gap: summary ? renderIndicator(summary.gap_budget, false, "Rp ") : "Rp 0",
      gapLabel: "Gap Budget"
    },
    {
      label: "Total Spent",
      value: summary ? formatRupiah(summary.total_spent) : "Rp 0",
      icon: DollarSign,
      color: "text-rose-600 bg-gradient-to-br from-rose-100 to-rose-50/50 border-rose-200 shadow-rose-50/50",
      mom: summary ? renderIndicator(summary.mom_realisasi_budget, true) : "0%",
      //yoy: summary ? renderIndicator(summary.growth_sales_vs_last_year_pct, true) : "0%",
      gap: summary ? renderIndicator(summary.compare_realisasi_budget, false, "Rp ") : "Rp 0",
      gapLabel: "Gap MoM"
    },
    {
      label: "Views",
      value: summary ? Number(summary.total_view).toLocaleString("id-ID") : "0",
      icon: AlertCircle,
      color: summary?.mom_view_prc >= 0 
        ? "text-emerald-600 bg-gradient-to-br from-emerald-100 to-emerald-50/50 border-emerald-200 shadow-emerald-50/50" 
        : "text-amber-600 bg-gradient-to-br from-amber-100 to-amber-50/50 border-amber-200 shadow-amber-50/50",
      mom: summary ? renderIndicator(summary.mom_view_prc, true) : "0%",
      //yoy: summary ? renderIndicator(summary.growth_sales_vs_last_year_pct, true) : "0%",
      gap: summary ? renderIndicator(summary.mom_views_diff, false) : "0",
      gapLabel: "Gap MoM"
    },
    {
      label: "Absurd",
      value: summary ? Number(summary.fase_1_views).toLocaleString("id-ID") : "0",
      icon: Receipt,
      color: "text-purple-600 bg-gradient-to-br from-purple-100 to-purple-50/50 border-purple-200 shadow-purple-50/50",
     mom: summary ? renderIndicator(summary.mom_view_prc_fase_1, true) : "0%",
      //yoy: summary ? renderIndicator(summary.growth_sales_vs_last_year_pct, true) : "0%",
      gap: summary ? renderIndicator(summary.mom_views_diff_fase_1, false) : "0",
      gapLabel: "Gap MoM"
    },
    {
      label: "Semi Absurd",
      value: summary ? Number(summary.fase_2_views).toLocaleString("id-ID") : "0",
      icon: Wallet,
      color: "text-cyan-600 bg-gradient-to-br from-cyan-100 to-cyan-50/50 border-cyan-200 shadow-cyan-50/50",
       mom: summary ? renderIndicator(summary.mom_view_prc_fase_2, true) : "0%",
      //yoy: summary ? renderIndicator(summary.growth_sales_vs_last_year_pct, true) : "0%",
      gap: summary ? renderIndicator(summary.mom_views_diff_fase_2, false) : "0",
      gapLabel: "Gap MoM"
    },
    {
      label: "Validasi",
      value: summary ? Number(summary.fase_3_views).toLocaleString("id-ID") : "0",
      icon: Coins,
      color: "text-yellow-600 bg-gradient-to-br from-yellow-100 to-yellow-50/50 border-yellow-200 shadow-yellow-50/50",
      mom: summary ? renderIndicator(summary.mom_view_prc_fase_3, true) : "0%",
      //yoy: summary ? renderIndicator(summary.growth_sales_vs_last_year_pct, true) : "0%",
      gap: summary ? renderIndicator(summary.mom_views_diff_fase_3, false) : "0",
      gapLabel: "Gap MoM"
    },
    {
      label: "CPV",
      value: summary && summary.total_spent > 0 ? formatRupiah(summary.total_spent / summary.total_view) : "Rp 0",
      icon: ShoppingBag,
      color: "text-emerald-600 bg-gradient-to-br from-emerald-100 to-emerald-50/50 border-emerald-200 shadow-emerald-50/50",
      mom: summary ? renderIndicatorCpv(momCpvPercent, true) : "0%",
  
  gap: summary ? renderIndicatorCpv(currentCPV-compareCPV, false) : "0",
      gapLabel: "Gap MoM"
    },
    {
      label: "Engagement",
      value: summary ? `${Number(summary.engagement).toLocaleString("id-ID")}` : "0",
      icon: Scale,
      color: "text-fuchsia-600 bg-gradient-to-br from-fuchsia-100 to-fuchsia-50/50 border-fuchsia-200 shadow-fuchsia-50/50",
      mom: summary ? renderIndicator((summary.gap_basket_vs_compare / Math.max(summary.basket_size - summary.gap_basket_vs_compare, 0.1)) * 100, true) : "0%",
      //yoy: summary ? renderIndicator(0, true) : "0%",
      gap: summary ? renderIndicator(summary.gap_basket_vs_compare, false) : "0",
      gapLabel: "Gap MoM"
    },
    {
      label: "Revenue",
      value: summary ? `${((Number(summary.mtd_sales) / Math.max(Number(summary.target), 1)) * 100).toFixed(0)}%` : "0%",
      icon: Activity,
      color: "text-indigo-600 bg-gradient-to-br from-indigo-100 to-indigo-50/50 border-indigo-200 shadow-indigo-50/50",
      mom: summary ? renderIndicator(summary.growth_sales_vs_compare_pct, true) : "0%",
      //yoy: summary ? renderIndicator(summary.growth_sales_vs_last_year_pct, true) : "0%",
      gap: summary ? `${summary.ach_to_full_moon.toFixed(0)}%` : "0%",
      gapLabel: "Ach. Rate"
    }
  ];

  const COLORS = ["#4f46e5", "#2563eb", "#0891b2", "#059669", "#d97706", "#dc2626"];
  
  return (
    <PageWrapper>
      {/* HEADER SECTION */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-6">
        <div>
          <h1 className="text-lg md:text-xl font-bold text-gray-900 tracking-tight">Marketing Performance</h1>
          <p className="text-xs text-gray-500">Kompilasi komparasi performa multi-dimensi penjualan toko</p>
        </div>

        {/* CONTROLLER SECTION */}
        <div className="flex flex-col sm:flex-row flex-wrap gap-2 w-full md:w-auto items-end">
          <div className="flex flex-col w-full sm:w-auto">
            <span className="text-[10px] font-bold text-gray-400 uppercase mb-1 tracking-wider">Bulan Berjalan (A)</span>
            <select
              value={currentMonth}
              onChange={(e) => setCurrentMonth(e.target.value)}
              className="w-full sm:w-auto border border-gray-200 rounded-xl px-3 py-1.5 text-xs bg-white font-medium text-gray-700 shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
            >
              {[...Array(12)].map((_, i) => (
                <option key={i} value={i + 1}>
                  {new Date(0, i).toLocaleString("en-US", { month: "long" })} 2026
                </option>
              ))}
            </select>
          </div>

          <div className="flex flex-col w-full sm:w-auto">
            <span className="text-[10px] font-bold text-gray-400 uppercase mb-1 tracking-wider">Bulan Perbandingan (B)</span>
            <select
              value={compareMonth}
              onChange={(e) => setCompareMonth(e.target.value)}
              className="w-full sm:w-auto border border-gray-200 rounded-xl px-3 py-1.5 text-xs bg-white font-medium text-gray-700 shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
            >
              {[...Array(12)].map((_, i) => (
                <option key={i} value={i + 1}>
                  {new Date(0, i).toLocaleString("en-US", { month: "long" })} 2026
                </option>
              ))}
            </select>
          </div>

          <div className="flex flex-col w-full sm:w-auto">
            <span className="text-[10px] font-bold text-gray-400 uppercase mb-1 tracking-wider">Bulan 1 Tahun Lalu (C)</span>
            <select
              value={lastYearMonth}
              onChange={(e) => setLastYearMonth(e.target.value)}
              className="w-full sm:w-auto border border-gray-200 rounded-xl px-3 py-1.5 text-xs bg-white font-medium text-gray-700 shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
            >
              {[...Array(12)].map((_, i) => (
                <option key={i} value={i + 1}>
                  {new Date(0, i).toLocaleString("en-US", { month: "long" })} 2025
                </option>
              ))}
            </select>
          </div>

          <button
            onClick={exportToExcel}
            className="w-full sm:w-auto px-4 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-semibold rounded-xl transition shadow-md shadow-indigo-100 active:scale-95 h-[34px] flex items-center justify-center gap-1.5"
          >
            Export Comprehensive Report
          </button>
        </div>
      </div>

      {/* THE MODERN & UNIFIED STATS CARD GRID WITH SMOOTH MOTION ANIMS */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 xl:grid-cols-9 gap-3">
        {stats.map((s, i) => {
          const Icon = s.icon;
          return (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              whileHover={{ y: -4, scale: 1.02 }}
              transition={{ duration: 0.35, ease: "easeOut", delay: i * 0.02 }}
              className="bg-white rounded-xl p-3 border border-gray-200/80 shadow-sm shadow-gray-100/50 hover:shadow-md hover:border-gray-300 transition-all duration-200 flex flex-col justify-between group relative overflow-hidden"
            >
              {/* Top Meta Line */}
              <div className="flex items-center justify-between gap-1 w-full mb-2">
                <span className="text-[9px] font-bold text-gray-400 uppercase tracking-wider truncate block">
                  {s.label}
                </span>
                <div className={`p-1.5 rounded-lg border shadow-inner shrink-0 group-hover:rotate-6 transition duration-200 ${s.color}`}>
                  <Icon size={13} className="stroke-[2.5]" />
                </div>
              </div>

              {/* Core Metric Value */}
              <div className="my-1.5">
                <h3 className="text-sm md:text-base xl:text-[16px] font-semibold text-gray-900 tracking-tight break-words">
                  {s.value}
                </h3>
              </div>

              {/* Layout Data Multi-dimensi: MoM, YoY, Gap */}
              <div className="mt-2 pt-2 border-t border-gray-100 flex flex-col gap-1 text-[10px] text-gray-500">
                <div className="flex justify-between items-center h-5">
                  <span>MoM:</span>
                  <span className="font-medium">{s.mom}</span>
                </div>
                {/* <div className="flex justify-between items-center h-5">
                  <span>YoY:</span>
                  <span className="font-medium">{s.yoy}</span>
                </div> */}
                <div className="flex justify-between items-center border-t border-dashed border-gray-100 pt-1 mt-0.5 h-6">
                  <span className="text-[9px] text-gray-400">{s.gapLabel}:</span>
                  <span className="font-medium text-right break-all">{s.gap}</span>
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>

      {/* LAYOUT 1 BARIS PAS - 6 KOLOM SECARA HORIZONTAL */}


      {/* MAIN GRID CHART GRAPHICS WITH MOTION ENTRANCE */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-6">
        {/* LEFT - LINE CHART */}
      {/* DAILY PERFORMANCE OVERVIEW (UPGRADED EXECUTIVE VERSION) */}
{/* DAILY PERFORMANCE OVERVIEW (REVENUE VS TARGET) */}


<motion.div 
  initial={{ opacity: 0, scale: 0.98 }}
  animate={{ opacity: 1, scale: 1 }}
  transition={{ duration: 0.4, delay: 0.2 }}
  className="bg-white p-6 rounded-2xl border border-gray-200/80 shadow-sm hover:shadow-md transition-all duration-300 flex flex-col justify-between"
>
  <div>
    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-4">
      <div>
        <h3 className="text-sm font-bold text-gray-800 tracking-wide flex items-center gap-2">
          <span>Daily Revenue vs Target Overview</span>
          <span className="bg-indigo-50 text-indigo-700 text-[10px] font-medium px-2 py-0.5 rounded-full border border-indigo-100">
            Target Tracking
          </span>
        </h3>
        <p className="text-[11px] text-gray-400 mt-0.5">
          Analisis komparasi omzet bersih (Net Revenue) terhadap target harian beserta capaian persentase keberhasilan (*achievement*).
        </p>
      </div>

      {/* Custom Legend Premium */}
      <div className="flex flex-wrap items-center gap-3 text-[11px] text-gray-500 bg-slate-50 px-3 py-1.5 rounded-xl border border-slate-100/80 self-start sm:self-center">
        <div className="flex items-center gap-1.5 cursor-pointer" onMouseEnter={() => setActiveLine("net_revenue")} onMouseLeave={() => setActiveLine(null)}>
          <span className={`w-2.5 h-2.5 rounded-full bg-[#4f46e5] transition-transform ${activeLine === 'net_revenue' ? 'scale-125' : ''}`} />
          <span className={activeLine === 'net_revenue' ? 'font-bold text-indigo-600' : ''}>Net Revenue</span>
        </div>
        <div className="flex items-center gap-1.5 cursor-pointer" onMouseEnter={() => setActiveLine("daily_target")} onMouseLeave={() => setActiveLine(null)}>
          <span className={`w-2.5 h-2.5 rounded-full bg-[#94a3b8] transition-transform ${activeLine === 'daily_target' ? 'scale-125' : ''}`} />
          <span className={activeLine === 'daily_target' ? 'font-bold text-slate-600' : ''}>Target</span>
        </div>
        <div className="flex items-center gap-1.5 cursor-pointer" onMouseEnter={() => setActiveLine("achievement_percent")} onMouseLeave={() => setActiveLine(null)}>
          <span className={`w-2.5 h-2.5 rounded-full bg-[#10b981] transition-transform ${activeLine === 'achievement_percent' ? 'scale-125' : ''}`} />
          <span className={activeLine === 'achievement_percent' ? 'font-bold text-emerald-600' : ''}>% Achieved</span>
        </div>
      </div>
    </div>
  </div>

  {/* Chart Area */}
  <div className="h-[320px] w-full">
    <ResponsiveContainer width="100%" height="100%">
      <LineChart 
        data={chartData} // Pastikan state chartData menampung response baru
        margin={{ top: 10, right: -5, left: -20, bottom: 0 }}
      >
        <CartesianGrid stroke="#f1f5f9" strokeDasharray="3 3" vertical={false} />
        
        <XAxis
          dataKey="date"
          tick={{ fontSize: 10, fill: "#64748b", fontWeight: 500 }}
          tickFormatter={(v) => {
            if (!v) return v;
            const parts = v.split('T')[0].split('-');
            if (parts.length < 3) return v;
            const months = ['Jan', 'Feb', 'Mar', 'Apr', 'Mei', 'Jun', 'Jul', 'Agu', 'Sep', 'Okt', 'Nov', 'Des'];
            return `${parts[2]} ${months[parseInt(parts[1], 10) - 1]}`;
          }}
          axisLine={false}
          tickLine={false}
        />
        
        {/* Sumbu Kiri: Nominal Finansial (Revenue & Target) */}
        <YAxis 
          yAxisId="left" 
          tick={{ fontSize: 10, fill: "#94a3b8" }} 
          axisLine={false} 
          tickLine={false} 
          tickFormatter={(v) => v >= 1e6 ? `${(v / 1e6).toFixed(0)}M` : v}
        />
        
        {/* Sumbu Kanan: Persentase Pencapaian Target */}
        <YAxis 
          yAxisId="right" 
          orientation="right" 
          tick={{ fontSize: 10, fill: "#94a3b8" }} 
          axisLine={false} 
          tickLine={false} 
          tickFormatter={(v) => `${v}%`}
        />

        {/* CUSTOM DETAILED TOOLTIP FOR TARGET MANAGEMENT */}
        <Tooltip
          cursor={{ stroke: "#e2e8f0", strokeWidth: 1.5, strokeDasharray: "4 4" }}
          content={({ active, payload, label }) => {
            if (active && payload && payload.length) {
              const data = payload[0].payload;
              const formattedDate = (() => {
                if (!label) return label;
                const parts = label.split('T')[0].split('-');
                if (parts.length < 3) return label;
                const months = ['Januari', 'Februari', 'Maret', 'April', 'Mei', 'Juni', 'Juli', 'Agustus', 'September', 'Oktober', 'November', 'Desember'];
                return `${parts[2]} ${months[parseInt(parts[1], 10) - 1]} ${parts[0]}`;
              })();

              const ach = Number(data.achievement_percent || 0);

              return (
                <div className="bg-white/95 backdrop-blur-md p-3.5 rounded-xl border border-gray-200 shadow-xl min-w-[220px] text-[11px]">
                  <p className="font-bold text-gray-800 border-b border-gray-100 pb-1.5 mb-2 text-xs text-center">
                    {formattedDate}
                  </p>
                  <div className="space-y-2">
                    <div className="flex justify-between items-center gap-4">
                      <span className="text-gray-500 flex items-center gap-1.5">
                        <span className="w-2 h-2 rounded-full bg-[#4f46e5]" /> Omzet Bersih (Net):
                      </span>
                      <span className="font-bold text-gray-900">
                        Rp {Number(data.net_revenue || 0).toLocaleString('id-ID')}
                      </span>
                    </div>
                    <div className="flex justify-between items-center gap-4">
                      <span className="text-gray-500 flex items-center gap-1.5">
                        <span className="w-2 h-2 rounded-full bg-[#94a3b8]" /> Target Harian:
                      </span>
                      <span className="font-semibold text-gray-600">
                        Rp {Number(data.daily_target || 0).toLocaleString('id-ID')}
                      </span>
                    </div>
                    <div className="flex justify-between items-center gap-4 border-t border-dotted border-gray-100 pt-1.5">
                      <span className="text-gray-500 flex items-center gap-1.5">
                        <span className="w-2 h-2 rounded-full bg-[#10b981]" /> Target Achievement:
                      </span>
                      <span className={`font-bold px-1.5 py-0.5 rounded-md ${ach >= 100 ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'}`}>
                        {ach.toFixed(2)}%
                      </span>
                    </div>
                  </div>
                </div>
              );
            }
            return null;
          }}
        />

        {/* Line Net Revenue */}
        <Line
          yAxisId="left"
          type="monotone"
          dataKey="net_revenue"
          name="Net Revenue"
          dot={false}
          activeDot={{ r: 5, strokeWidth: 1, fill: "#4f46e5" }}
          stroke="#4f46e5"
          strokeWidth={activeLine === "net_revenue" ? 3.5 : 2.5}
          opacity={activeLine && activeLine !== "net_revenue" ? 0.3 : 1}
        />
        
        {/* Line Target Harian */}
        <Line 
          yAxisId="left" 
          type="monotone" 
          dataKey="daily_target" 
          name="Target Harian"
          dot={false} 
          activeDot={{ r: 5, strokeWidth: 1, fill: "#94a3b8" }}
          stroke="#94a3b8" 
          strokeWidth={activeLine === "daily_target" ? 3.5 : 2.5}
          strokeDasharray="4 4"
          opacity={activeLine && activeLine !== "daily_target" ? 0.3 : 1}
        />
        
        {/* Line Achievement Rate */}
        <Line 
          yAxisId="right" 
          type="monotone" 
          dataKey="achievement_percent" 
          name="% Achievement"
          dot={false} 
          activeDot={{ r: 5, strokeWidth: 1, fill: "#10b981" }}
          stroke="#10b981" 
          strokeWidth={activeLine === "achievement_percent" ? 3.5 : 2.5}
          opacity={activeLine && activeLine !== "achievement_percent" ? 0.3 : 1}
        />
      </LineChart>
    </ResponsiveContainer>
  </div>
</motion.div>



        {/* RIGHT - YoY CHART (RE-ENGINEERED FOR GAP & GROWTH VISUALIZATION) */}
       {/* RIGHT - YoY & MoM PERFORMANCE CHART */}
       {/* RIGHT - YoY & MoM PERFORMANCE CHART (UPGRADED EXECUTIVE VERSION) */}
<motion.div 
  initial={{ opacity: 0, scale: 0.98 }}
  animate={{ opacity: 1, scale: 1 }}
  transition={{ duration: 0.4, delay: 0.25 }}
  className="bg-white p-6 rounded-2xl border border-gray-200/80 shadow-sm hover:shadow-md transition relative flex flex-col justify-between"
>
  <div>
    <div className="flex items-start justify-between mb-2">
      <div>
        <h3 className="text-sm font-bold text-gray-800 tracking-wide flex items-center gap-2">
          <span>YoY & MoM Growth Gap Analysis</span>
          <span className="bg-indigo-50 text-indigo-700 text-[10px] font-medium px-2 py-0.5 rounded-full border border-indigo-100">
            Dinamis
          </span>
        </h3>
        <p className="text-[11px] text-gray-400 mt-0.5">
          Komparasi performa omzet tahunan, bulanan, volume transaksi, beserta margin kesehatan pertumbuhan perusahaan.
        </p>
      </div>
      
      {/* Dynamic Floating Highlight Badge */}
      <div className="flex gap-2 shrink-0">
        <div className="bg-slate-50 border border-slate-100 shadow-sm rounded-xl px-3 py-1.5 text-right min-w-[75px]">
          <div className="text-gray-400 font-semibold text-[9px] uppercase tracking-wider">YoY {selectedData?.month ? selectedData.month.split('-')[1] : "Tren"}</div>
          <div className={`font-bold text-xs mt-0.5 flex items-center justify-end gap-0.5 ${(selectedData?.growth || 0) >= 0 ? "text-emerald-600" : "text-rose-600"}`}>
            {(selectedData?.growth || 0) >= 0 ? "▲" : "▼"} {Math.abs(selectedData?.growth || 0).toFixed(1)}%
          </div>
        </div>
        <div className="bg-slate-50 border border-slate-100 shadow-sm rounded-xl px-3 py-1.5 text-right min-w-[75px]">
          <div className="text-gray-400 font-semibold text-[9px] uppercase tracking-wider">MoM {selectedData?.month ? selectedData.month.split('-')[1] : "Tren"}</div>
          <div className={`font-bold text-xs mt-0.5 flex items-center justify-end gap-0.5 ${(selectedData?.momGrowth || 0) >= 0 ? "text-cyan-600" : "text-amber-600"}`}>
            {(selectedData?.momGrowth || 0) >= 0 ? "▲" : "▼"} {Math.abs(selectedData?.momGrowth || 0).toFixed(1)}%
          </div>
        </div>
      </div>
    </div>

    {/* Info Ringkas Tren */}
    <div className="flex items-center gap-4 border-b border-gray-100 pb-3 mb-4 text-[11px] text-gray-500">
      <div className="flex items-center gap-1.5">
        <span className="w-2.5 h-2.5 rounded-sm bg-[#4f46e5]" />
        <span>Tahun Ini (Gross)</span>
      </div>
      <div className="flex items-center gap-1.5">
        <span className="w-2.5 h-2.5 rounded-sm bg-[#cbd5e1]" />
        <span>Tahun Lalu (Gross)</span>
      </div>
      <div className="flex items-center gap-1.5">
        <span className="w-3 h-0.5 bg-[#10b981]" />
        <span className="font-medium text-emerald-600">YoY Rate (%)</span>
      </div>
      <div className="flex items-center gap-1.5">
        <span className="w-3 h-0.5 border-t-2 border-dashed border-[#06b6d4]" />
        <span className="font-medium text-cyan-600">MoM Rate (%)</span>
      </div>
    </div>
  </div>

  {/* Chart Container */}
  <div className="h-[300px] w-100">
    <ResponsiveContainer width="100%" height="100%">
      <ComposedChart
        data={yoyData}
        margin={{ top: 10, right: -5, left: -20, bottom: 0 }}
        onClick={(e) => { 
          if (e && e.activeLabel) {
            setSelectedMonth(e.activeLabel);
          }
        }}
      >
        <CartesianGrid stroke="#f1f5f9" strokeDasharray="3 3" vertical={false} />
        
        <XAxis 
          dataKey="month" 
          tickFormatter={(v) => {
            const parts = v.split('-');
            const months = ['Jan', 'Feb', 'Mar', 'Apr', 'Mei', 'Jun', 'Jul', 'Agu', 'Sep', 'Okt', 'Nov', 'Des'];
            return parts[1] ? months[parseInt(parts[1], 10) - 1] : v;
          }}
          tick={{ fontSize: 10, fill: "#64748b", fontWeight: 500 }} 
          axisLine={false} 
          tickLine={false} 
        />
        
        {/* Sumbu Kiri: Keuangan Finansial */}
        <YAxis 
          yAxisId="left" 
          tick={{ fontSize: 10, fill: "#94a3b8" }} 
          axisLine={false} 
          tickLine={false} 
          tickFormatter={(v) => v >= 1e9 ? `${(v / 1e9).toFixed(1)}B` : `${(v / 1e6).toFixed(0)}M`}
        />
        
        {/* Sumbu Kanan: Persentase Pertumbuhan */}
        <YAxis 
          yAxisId="right" 
          orientation="right" 
          tick={{ fontSize: 10, fill: "#94a3b8" }} 
          axisLine={false} 
          tickLine={false} 
          tickFormatter={(v) => `${v}%`} 
        />
        
        {/* 🔥 HIGHLY DETAILED CUSTOM TOOLTIP */}
        <Tooltip
          cursor={{ fill: '#f8fafc' }}
          content={({ active, payload, label }) => {
            if (active && payload && payload.length) {
              const data = payload[0].payload;
              const formattedLabel = (() => {
                const parts = label.split('-');
                const months = ['Januari', 'Februari', 'Maret', 'April', 'Mei', 'Juni', 'Juli', 'Agustus', 'September', 'Oktober', 'November', 'Desember'];
                return parts[1] ? `${months[parseInt(parts[1], 10) - 1]} ${parts[0]}` : label;
              })();

              return (
                <div className="bg-white/95 backdrop-blur-md p-4 rounded-xl border border-gray-200 shadow-xl max-w-xs text-[11px]">
                  <p className="font-bold text-gray-800 border-b border-gray-100 pb-1.5 mb-2 text-xs text-center">{formattedLabel}</p>
                  
                  {/* Bagian Perbandingan Nominal */}
                  <div className="space-y-1.5 mb-2.5">
                    <div className="flex justify-between gap-6">
                      <span className="text-gray-500 font-medium">Gross Sales (This Year):</span>
                      <span className="font-bold text-gray-900">Rp {data.thisYear.toLocaleString('id-ID')}</span>
                    </div>
                    <div className="flex justify-between gap-6">
                      <span className="text-gray-500">Gross Sales (Last Year):</span>
                      <span className="font-semibold text-gray-600">Rp {data.lastYear.toLocaleString('id-ID')}</span>
                    </div>
                    <div className="flex justify-between gap-6 border-t border-dotted border-gray-100 pt-1">
                      <span className="text-gray-500 font-medium">Net Sales (This Year):</span>
                      <span className="font-bold text-indigo-600">Rp {data.netThis.toLocaleString('id-ID')}</span>
                    </div>
                    <div className="flex justify-between gap-6">
                      <span className="text-gray-500">Total Transaksi (Trx):</span>
                      <span className="font-semibold text-gray-700">{data.trxThis.toLocaleString('id-ID')} trx</span>
                    </div>
                  </div>

                  {/* Bagian Skor Persentase Indikator */}
                  <div className="grid grid-cols-2 gap-2 border-t border-gray-100 pt-2 text-center">
                    <div className={`p-1.5 rounded-lg ${data.growth >= 0 ? "bg-emerald-50 text-emerald-700" : "bg-rose-50 text-rose-700"}`}>
                      <div className="text-[9px] font-medium opacity-80">YoY Growth</div>
                      <div className="font-bold text-xs">{data.growth >= 0 ? "▲" : "▼"} {data.growth.toFixed(1)}%</div>
                    </div>
                    <div className={`p-1.5 rounded-lg ${data.momGrowth >= 0 ? "bg-cyan-50 text-cyan-700" : "bg-amber-50 text-amber-700"}`}>
                      <div className="text-[9px] font-medium opacity-80">MoM Growth</div>
                      <div className="font-bold text-xs">{data.momGrowth >= 0 ? "▲" : "▼"} {data.momGrowth.toFixed(1)}%</div>
                    </div>
                  </div>
                </div>
              );
            }
            return null;
          }}
        />
        
        {/* Batang Data Grafik */}
        <Bar yAxisId="left" dataKey="lastYear" fill="#cbd5e1" radius={[3, 3, 0, 0]} maxBarSize={16} opacity={0.85} />
        <Bar yAxisId="left" dataKey="thisYear" fill="#4f46e5" radius={[3, 3, 0, 0]} maxBarSize={16} />
        
        {/* Garis Dasar Nol Persen */}
        <ReferenceLine yAxisId="right" y={0} stroke="#e2e8f0" strokeWidth={1} />
        
        {/* Garis Tren Pertumbuhan */}
        <Line yAxisId="right" type="monotone" dataKey="growth" stroke="#10b981" strokeWidth={3} dot={{ r: 2.5, strokeWidth: 1, fill: "#fff" }} activeDot={{ r: 4 }} />
        <Line yAxisId="right" type="monotone" dataKey="momGrowth" stroke="#06b6d4" strokeWidth={2} strokeDasharray="4 4" dot={{ r: 2.5, strokeWidth: 1, fill: "#fff" }} activeDot={{ r: 4 }} />
      </ComposedChart>
    </ResponsiveContainer>
  </div>
</motion.div>

{/* CARD LIST MULTI-DIMENSIONAL GROWTH & GAP PER TOKO */}



      </div>

   {/* 1. UBAH DI SINI: pb-3 dihilangkan jika tidak scroll, diganti ke pb-1 agar rapat */}
<div className="col-span-1 lg:col-span-2 w-full overflow-x-auto pb-1 mt-4 custom-scrollbar">
  <div className="grid grid-cols-6 min-w-[1100px] lg:min-w-full gap-3.5">
    {(() => {
      const groupedByIdMk = marketingRawData.reduce((acc, item) => {
        const idMk = item.brand || "UNKNOWN";
        if (!acc[idMk]) {
          acc[idMk] = [];
        }
        acc[idMk].push({
          ...item,
          budget_realisasi: Number(item.current_realisasi_budget || 0),
          total_views: Number(item.current_views || 0)
        });
        return acc;
      }, {});

      if (Object.keys(groupedByIdMk).length === 0) {
        return (
          <div className="bg-slate-50/50 p-6 rounded-2xl border border-slate-100 flex items-center justify-center text-slate-400 font-medium col-span-6 h-[180px]">
            Tidak ada data marketing pada periode ini
          </div>
        );
      }

      return Object.entries(groupedByIdMk).map(([idMk, items], index) => {
        const sortedItems = items.sort((a, b) => String(a.fase).localeCompare(String(b.fase)));
        const totalBudget = sortedItems.reduce((sum, i) => sum + i.budget_realisasi, 0);

        return (
          <motion.div
            key={idMk}
            initial={{ opacity: 0, y: 15, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            whileHover={{ y: -4, transition: { duration: 0.2 } }}
            transition={{ type: "spring", stiffness: 100, damping: 15, delay: index * 0.05 }}
            className="bg-white p-3.5 rounded-2xl border border-slate-200/70 shadow-sm hover:shadow-md hover:border-slate-300/90 transition-all duration-300 flex flex-col justify-between"
          >
            <div>
              <div className="flex items-center justify-between border-b border-slate-100 pb-2 mb-2.5">
                <div className="flex flex-col">
                  <span className="text-[12px] font-bold text-slate-700 tracking-tight truncate max-w-[80px]">
                    {idMk}
                  </span>
                  <span className="text-[9px] text-slate-400 font-medium">
                    Spent: Rp {(totalBudget / 1e6).toFixed(1)}M
                  </span>
                </div>
                <span className="bg-slate-50 text-slate-500 text-[8px] font-semibold px-1.5 py-0.5 rounded-md border border-slate-100">
                  {items.length} Phs
                </span>
              </div>

              {/* 2. UBAH DI SINI: -ml-5 dihilangkan agar container tidak bergeser keluar jalur bawah */}
              <div className="h-[135px] w-full text-[8px]">
                {/* 3. UBAH DI SINI: width dikembalikan ke murni 100% agar tidak menciptakan margin hantu */}
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart
                    data={sortedItems}
                    /* margin left disesuaikan ke -30 agar teks YAxis tetap terlihat walaupun width 100% */
                    margin={{ top: 10, right: -5, left: -30, bottom: -5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                    
                    <XAxis
                      dataKey="fase"
                      tickLine={false}
                      axisLine={false}
                      stroke="#94a3b8"
                      tickFormatter={(val) => val.replace("FASE ", "F")}
                      tick={{ fill: '#64748b', fontSize: 8, fontWeight: 500 }}
                    />
                    
                    <YAxis
                      yAxisId="left"
                      tickLine={false}
                      axisLine={false}
                      stroke="#64748b"
                      tickFormatter={(val) => `${(val / 1e6).toFixed(0)}M`}
                      tick={{ fill: '#94a3b8', fontSize: 7 }}
                    />
                    
                    <YAxis
                      yAxisId="right"
                      orientation="right"
                      tickLine={false}
                      axisLine={false}
                      stroke="#0d9488"
                      tickFormatter={(val) => val >= 1e6 ? `${(val / 1e6).toFixed(0)}M` : val.toLocaleString()}
                      tick={{ fill: '#94a3b8', fontSize: 7 }}
                    />
                    
                    <Tooltip
                      contentStyle={{
                        backgroundColor: 'rgba(255, 255, 255, 0.96)',
                        borderRadius: '10px',
                        border: '1px solid #e2e8f0',
                        color: '#334155',
                        fontSize: '9px',
                        padding: '6px 10px',
                        boxShadow: '0 4px 12px -2px rgb(0 0 0 / 0.05)'
                      }}
                      formatter={(value, name) => {
                        if (name === "Budget") return [formatRupiah(value), name];
                        return [Number(value).toLocaleString("id-ID") + " cls", "Views"];
                      }}
                    />

                    <Bar
                      yAxisId="left"
                      dataKey="budget_realisasi"
                      name="Budget"
                      fill="#94a3b8"
                      radius={[3, 3, 0, 0]}
                      barSize={12}
                      animationDuration={1200}
                    />
                    
                    <Line
                      yAxisId="right"
                      type="monotone"
                      dataKey="total_views"
                      name="Views"
                      stroke="#0d9488"
                      strokeWidth={2}
                      dot={{ r: 2, strokeWidth: 1.5, fill: "#fff", stroke: "#0d9488" }}
                      activeDot={{ r: 4, strokeWidth: 0 }}
                      animationDuration={1500}
                    />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            </div>
          </motion.div>
        );
      });
    })()}
  </div>
</div>



{/* ========================================== */}
{/* NEW: WRAPPED CONTENT PERFORMANCE CARD */}
{/* ========================================== */}
<div className="mt-8 mb-6 bg-white rounded-2xl border border-slate-200/80 shadow-sm overflow-hidden">
  {/* Card Header & Tabs Section */}
  <div className="p-4 sm:p-5 border-b border-slate-100 bg-gradient-to-b from-slate-50/50 to-white">
    {/* Header Title & Info */}
    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
      <div className="flex flex-col">
        <h3 className="text-sm font-bold text-slate-800 tracking-wide flex items-center gap-2">
          <span>Top Content Performance Analytics</span>
          <span className="bg-emerald-50 text-emerald-700 text-[10px] font-semibold px-2 py-0.5 rounded-full border border-emerald-100/60">
            Engagement Tracker
          </span>
        </h3>
        <p className="text-[11px] text-slate-400 mt-0.5">
          Daftar sebaran konten kreatif berdasarkan interaksi total penonton (*views, likes, comments, shares*).
        </p>
      </div>
      
      {/* Indikator Total Konten */}
      {contentPerformanceData && contentPerformanceData.length > 0 && (
        <div className="text-[10px] font-medium text-slate-500 bg-slate-50 border border-slate-200/60 px-2.5 py-1 rounded-xl self-start sm:self-auto">
          Total Konten: <span className="font-bold text-indigo-600">{contentPerformanceData.length}</span>
        </div>
      )}
    </div>

    {/* 🔥 SISTEM GROUPING TAB BERDASARKAN ID MK */}
    {contentPerformanceData && contentPerformanceData.length > 0 && (
      <div className="w-full overflow-x-auto pb-1 custom-scrollbar">
        <div className="flex items-center gap-1.5 min-w-max">
          {/* Tab Default untuk Menampilkan Semua */}
          <button
            onClick={() => setActiveContentTab("ALL")}
            className={`px-3 py-1.5 rounded-xl text-[11px] font-bold tracking-tight transition-all duration-200 border ${
              activeContentTab === "ALL"
                ? "bg-slate-900 text-white border-slate-900 shadow-sm"
                : "bg-white text-slate-500 border-slate-200 hover:bg-slate-50 hover:text-slate-700"
            }`}
          >
            Semua Konten ({contentPerformanceData.length})
          </button>

          {/* Generate Tab Secara Dinamis Berdasarkan ID MK */}
          {Array.from(new Set(contentPerformanceData.map((c) => c.brand || "UNKNOWN")))
            .filter(Boolean)
            .map((idMk) => {
              const countPerMk = contentPerformanceData.filter((c) => (c.brand || "UNKNOWN") === idMk).length;
              return (
                <button
                  key={idMk}
                  onClick={() => setActiveContentTab(idMk)}
                  className={`px-3 py-1.5 rounded-xl text-[11px] font-bold tracking-tight transition-all duration-200 border ${
                    activeContentTab === idMk
                      ? "bg-indigo-600 text-white border-indigo-600 shadow-sm"
                      : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50 hover:text-slate-800"
                  }`}
                >
                  {idMk} <span className={`ml-1 text-[9px] font-medium ${activeContentTab === idMk ? "text-indigo-200" : "text-slate-400"}`}>({countPerMk})</span>
                </button>
              );
            })}
        </div>
      </div>
    )}
  </div>

  {/* Card Body: Area Scroll Content Cards */}
    <div className="p-4 sm:p-5 bg-slate-50/30">
  <div className="w-full overflow-x-auto pb-2 pt-1 custom-scrollbar snap-x snap-mandatory">
    <div className="flex gap-4 min-w-max pr-4">
      {contentPerformanceData && contentPerformanceData.length > 0 ? (
        (() => {
          const filteredContent = contentPerformanceData.filter((content) => {
            if (activeContentTab === "ALL") return true;
            return (content.brand || "UNKNOWN") === activeContentTab;
          });

          if (filteredContent.length === 0) {
            return (
              <div className="bg-white p-6 rounded-2xl border border-slate-200/60 flex items-center justify-center text-slate-400 font-medium w-full h-[140px] text-xs shadow-xs">
                Tidak ada data konten untuk ID MK ini
              </div>
            );
          }

          return filteredContent.map((content, idx) => {
            const platform = (content.medsos || content.platform || "").toLowerCase();

            return (
              <motion.div
                key={content.content_id || idx}
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                whileHover={{ y: -5, transition: { duration: 0.2 } }}
                transition={{ duration: 0.25, delay: Math.min(idx * 0.02, 0.2) }}
                onClick={() => setSelectedContent(content)} // 🔥 TRIGGER POPUP
                className="w-[220px] bg-white rounded-2xl border border-slate-200/80 shadow-xs hover:shadow-md hover:border-slate-300 transition-all duration-300 overflow-hidden flex flex-col justify-between snap-start group cursor-pointer"
              >
                {/* Image Section */}
                <div className="relative h-36 w-full bg-slate-50 overflow-hidden border-b border-slate-100">
                  {content.img_content ? (
                    <img
                      src={content.img_content}
                      alt={content.brand}
                      crossOrigin="anonymous"
                      className="w-full h-full object-cover group-hover:scale-105 transition duration-500"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = "https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?w=500&auto=format&fit=crop&q=60";
                      }}
                    />
                  ) : (
                    <div className="w-full h-full flex flex-col items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100/50 text-slate-400 gap-1">
                      <svg className="w-5 h-5 opacity-60" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 002-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      <span className="text-[9px] font-medium tracking-tight">No Image Preview</span>
                    </div>
                  )}
                  
                  {/* Badge Fase */}
                  <span className="absolute top-2 left-2 bg-white/90 backdrop-blur-md text-slate-800 text-[8px] font-bold px-2 py-0.5 rounded-lg shadow-xs border border-slate-200/50">
                    {content.fase}
                  </span>
                  
                  {/* ID MK Badge */}
                  <span className="absolute top-2 right-2 bg-indigo-600/95 backdrop-blur-xs text-white text-[8px] font-bold px-1.5 py-0.5 rounded-md shadow-xs max-w-[80px] truncate">
                    {content.brand}
                  </span>

                  {/* Medsos Icon */}
                  <div className="absolute bottom-2 right-2 p-1 bg-white/95 backdrop-blur-xs rounded-lg shadow-xs border border-slate-100 flex items-center justify-center">
                    {platform === "instagram" || platform === "ig" ? (
                      <svg className="w-3.5 h-3.5 text-pink-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <rect x="2" y="2" width="20" height="20" rx="5" ry="5"></rect>
                        <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"></path>
                        <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"></line>
                      </svg>
                    ) : platform === "tiktok" ? (
                      <svg className="w-3.5 h-3.5 text-slate-900" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.03 1.76 4.05 1.15.96 2.66 1.41 4.16 1.48v3.91c-1.34-.04-2.67-.4-3.82-1.12-.55-.35-1.04-.79-1.45-1.3-.06 1.94-.04 3.88-.05 5.82-.04 2.14-.52 4.34-1.85 6.03-1.53 2-4.04 3.08-6.52 2.92-2.39-.08-4.75-1.33-5.95-3.41-1.36-2.25-1.27-5.32.26-7.46 1.34-1.92 3.69-3.04 6.03-2.82v4.03c-1.15-.17-2.39.26-3.05 1.22-.68.9-.66 2.27.06 3.12.72.93 2.01 1.25 3.07.81.75-.28 1.28-.97 1.37-1.77.06-1.54.03-3.08.04-4.62.01-4.08-.01-8.16.02-12.24z"/>
                      </svg>
                    ) : (
                      <svg className="w-3.5 h-3.5 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5l1.5-1.5m-.346-9.017a4 4 0 00-5.656 0l-4 4a4 4 0 005.656 5l1.5-1.5" />
                      </svg>
                    )}
                  </div>
                </div>

                {/* Content Body */}
                <div className="p-3 flex-1 flex flex-col justify-between bg-white">
                  <div>
                    {/* KOL Name */}
                    <div className="flex items-center gap-1 mb-1.5 max-w-full overflow-hidden">
                      <svg className="w-3 h-3 text-indigo-500 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                      <span className="text-[10px] font-bold text-slate-700 truncate tracking-wide group-hover:text-indigo-600 transition duration-150">
                        {content.kol_name || "Unknown Creator"}
                      </span>
                    </div>

                    <p className="text-[10.5px] text-slate-500 line-clamp-2 font-medium mb-3 min-h-[32px] leading-relaxed">
                      {content.description ? `"${content.description}"` : 'Tidak ada deskripsi konten.'}
                    </p>
                  </div>

                  {/* Stats Grid */}
                  <div>
                    <div className="grid grid-cols-2 gap-1.5 pt-2.5 border-t border-slate-100/80">
                      <div className="bg-slate-50/70 p-1.5 rounded-xl border border-slate-100 text-center">
                        <span className="text-[8px] font-bold text-slate-400 uppercase tracking-wider block scale-90">Views</span>
                        <span className="text-[11px] font-extrabold text-slate-700 mt-0.5">
                          {Number(content.play_count || 0).toLocaleString("id-ID")}
                        </span>
                      </div>
                      <div className="bg-emerald-50/40 p-1.5 rounded-xl border border-emerald-100/30 text-center">
                        <span className="text-[8px] font-bold text-emerald-500/80 uppercase tracking-wider block scale-90">Likes</span>
                        <span className="text-[11px] font-extrabold text-emerald-600 mt-0.5">
                          {Number(content.like_count || 0).toLocaleString("id-ID")}
                        </span>
                      </div>
                      <div className="bg-cyan-50/40 p-1.5 rounded-xl border border-cyan-100/30 text-center">
                        <span className="text-[8px] font-bold text-cyan-500/80 uppercase tracking-wider block scale-90">Comments</span>
                        <span className="text-[11px] font-extrabold text-cyan-600 mt-0.5">
                          {Number(content.comment_count || 0).toLocaleString("id-ID")}
                        </span>
                      </div>
                      <div className="bg-indigo-50/40 p-1.5 rounded-xl border border-indigo-100/30 text-center">
                        <span className="text-[8px] font-bold text-indigo-500/80 uppercase tracking-wider block scale-90">Shares</span>
                        <span className="text-[11px] font-extrabold text-indigo-600 mt-0.5">
                          {Number(content.share_count || 0).toLocaleString("id-ID")}
                        </span>
                      </div>
                    </div>

                    <div className="text-[8px] text-right text-slate-400 font-medium mt-2">
                      {content.content_date ? new Date(content.content_date).toLocaleDateString("id-ID", {
                        day: "numeric",
                        month: "short",
                        year: "numeric"
                      }) : "-"}
                    </div>
                  </div>
                </div>
              </motion.div>
            );
          });
        })()
      ) : (
        <div className="bg-white p-6 rounded-2xl border border-slate-200/60 flex items-center justify-center text-slate-400 font-medium w-full h-[140px] text-xs shadow-xs">
          Tidak ada data performa konten pada periode ini
        </div>
      )}
    </div>
  </div>
</div>

{/* ========================================== */}
{/* NEW: POPUP MODAL DETAIL KONTEN */}
{/* ========================================== */}
<AnimatePresence>
  {selectedContent && (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6 isolate">
      {/* Backdrop Hitam Transparan dengan Efek Blur Lebih Kuat */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={() => setSelectedContent(null)}
        className="fixed inset-0 bg-slate-900/40 backdrop-blur-md" 
      />

      {/* Konten Modal */}
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 20 }}
        transition={{ type: "spring", duration: 0.4 }}
        className="relative bg-white w-full max-w-lg rounded-3xl shadow-xl border border-slate-100 overflow-hidden z-10 flex flex-col auto-rows-max"
      >
        {/* Header Modal Image Preview */}
        <div className="relative h-56 bg-slate-100 overflow-hidden border-b border-slate-100">
          <img
            src={selectedContent.img_content || "https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?w=500&auto=format&fit=crop&q=60"}
            alt={selectedContent.brand}
            crossOrigin="anonymous"
            className="w-full h-full object-cover"
          />
          {/* Top Badges */}
          <div className="absolute top-4 left-4 flex gap-2">
            <span className="bg-white/95 backdrop-blur-md text-slate-800 text-[10px] font-extrabold px-2.5 py-1 rounded-xl shadow-xs border border-slate-200/50">
              {selectedContent.fase}
            </span>
            <span className="bg-indigo-600 text-white text-[10px] font-extrabold px-2.5 py-1 rounded-xl shadow-xs">
              {selectedContent.brand}
            </span>
          </div>

          {/* Close Button */}
          <button
            onClick={() => setSelectedContent(null)}
            className="absolute top-4 right-4 p-2 bg-slate-900/40 hover:bg-slate-900/60 text-white backdrop-blur-md rounded-full transition-all duration-200 shadow-sm"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Modal Body */}
        <div className="p-5 sm:p-6 flex-1 overflow-y-auto">
          {/* Nama KOL & Info Medsos */}
          <div className="flex items-center justify-between gap-4 mb-3 pb-3 border-b border-slate-100">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-indigo-50 flex items-center justify-center text-indigo-600 font-bold text-xs border border-indigo-100">
                {selectedContent.kol_name ? selectedContent.kol_name.charAt(0).toUpperCase() : "K"}
              </div>
              <div className="flex flex-col">
                <span className="text-xs font-bold text-slate-800 leading-tight">
                  {selectedContent.kol_name || "Unknown Creator"}
                </span>
                <span className="text-[10px] text-slate-400 mt-0.5 capitalize font-medium">
                  Platform: {selectedContent.medsos || selectedContent.platform || "-"}
                </span>
              </div>
            </div>

            {/* Date Badge */}
            <div className="text-[10px] text-slate-400 font-semibold bg-slate-50 border border-slate-200/50 px-2 py-1 rounded-lg">
              {selectedContent.content_date ? new Date(selectedContent.content_date).toLocaleDateString("id-ID", {
                day: "numeric",
                month: "long",
                year: "numeric"
              }) : "-"}
            </div>
          </div>

          {/* Deskripsi Lengkap */}
          <div className="mb-5">
            <h4 className="text-[10px] font-extrabold text-slate-400 uppercase tracking-wider mb-1.5">Caption Konten</h4>
            <div className="bg-slate-50/80 border border-slate-100 rounded-xl p-3 text-xs text-slate-600 font-medium leading-relaxed max-h-[100px] overflow-y-auto custom-scrollbar">
              {selectedContent.description ? `"${selectedContent.description}"` : "Tidak ada deskripsi konten."}
            </div>
          </div>

          {/* Grand Stats Matrix */}
          <div>
            <h4 className="text-[10px] font-extrabold text-slate-400 uppercase tracking-wider mb-2.5">Performance Metrics</h4>
            <div className="grid grid-cols-2 gap-2.5">
              <div className="bg-slate-50/80 p-2.5 rounded-xl border border-slate-100 flex items-center gap-3">
                <div className="p-1.5 bg-slate-200/50 text-slate-600 rounded-lg shrink-0">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                </div>
                <div>
                  <span className="text-[9px] font-bold text-slate-400 block uppercase tracking-wide">Views</span>
                  <span className="text-sm font-extrabold text-slate-800">{Number(selectedContent.play_count || 0).toLocaleString("id-ID")}</span>
                </div>
              </div>

              <div className="bg-emerald-50/40 p-2.5 rounded-xl border border-emerald-100/30 flex items-center gap-3">
                <div className="p-1.5 bg-emerald-100/60 text-emerald-600 rounded-lg shrink-0">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" /></svg>
                </div>
                <div>
                  <span className="text-[9px] font-bold text-emerald-500 block uppercase tracking-wide">Likes</span>
                  <span className="text-sm font-extrabold text-emerald-700">{Number(selectedContent.like_count || 0).toLocaleString("id-ID")}</span>
                </div>
              </div>

              <div className="bg-cyan-50/40 p-2.5 rounded-xl border border-cyan-100/30 flex items-center gap-3">
                <div className="p-1.5 bg-cyan-100/60 text-cyan-600 rounded-lg shrink-0">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" /></svg>
                </div>
                <div>
                  <span className="text-[9px] font-bold text-cyan-500 block uppercase tracking-wide">Comments</span>
                  <span className="text-sm font-extrabold text-cyan-700">{Number(selectedContent.comment_count || 0).toLocaleString("id-ID")}</span>
                </div>
              </div>

              <div className="bg-indigo-50/40 p-2.5 rounded-xl border border-indigo-100/30 flex items-center gap-3">
                <div className="p-1.5 bg-indigo-100/60 text-indigo-600 rounded-lg shrink-0">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" /></svg>
                </div>
                <div>
                  <span className="text-[9px] font-bold text-indigo-500 block uppercase tracking-wide">Shares</span>
                  <span className="text-sm font-extrabold text-indigo-700">{Number(selectedContent.share_count || 0).toLocaleString("id-ID")}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer Action Button */}
        {selectedContent.content_link && (
          <div className="p-4 bg-slate-50 border-t border-slate-100 flex gap-2">
            <a
              href={selectedContent.content_link}
              target="_blank"
              rel="noopener noreferrer"
              className="w-full bg-slate-900 hover:bg-slate-800 text-white font-bold text-xs py-3 px-4 rounded-xl shadow-xs hover:shadow-sm text-center transition-all duration-200 flex items-center justify-center gap-2"
            >
              <span>Kunjungi Tautan Konten Asli</span>
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
            </a>
          </div>
        )}
      </motion.div>
    </div>
  )}
</AnimatePresence>


</div>


{/* ========================================================================= */}
<div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mt-6 mb-8">
  {(() => {
    // 1. Proses & Akumulasikan semua data interaksi per ID MK global
    const globalMkDataMap = (contentPerformanceData || []).reduce((acc, item) => {
      const idMk = item.brand || "UNKNOWN";
      if (!acc[idMk]) {
        acc[idMk] = { brand: idMk, total_likes: 0, total_shares: 0, count: 0 };
      }
      acc[idMk].total_likes += Number(item.like_count || 0);
      acc[idMk].total_shares += Number(item.share_count || 0);
      acc[idMk].count += 1;
      return acc;
    }, {});

    const chartDataGlobal = Object.values(globalMkDataMap).sort((a, b) => a.brand.localeCompare(b.brand));

    // 2. Proses Agregasi data KOL berdasarkan views, likes, dan shares terbanak
    const kolLeaderboard = (contentPerformanceData || []).reduce((acc, item) => {
      const name = item.kol_name || "Unknown KOL";
      if (!acc[name]) {
        acc[name] = { name, views: 0, likes: 0, shares: 0 };
      }
      acc[name].views += Number(item.view_count || item.play_count || 0);
      acc[name].likes += Number(item.like_count || 0);
      acc[name].shares += Number(item.share_count || 0);
      return acc;
    }, {});

    const sortedKols = Object.values(kolLeaderboard)
      .sort((a, b) => (b.views + b.likes + b.shares) - (a.views + a.likes + a.shares))
      .slice(0, 5);

    if (chartDataGlobal.length === 0) return null;

    return (
      <>
        {/* CARD GRAPH 1: TOTAL LIKES ALL MK */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="bg-white p-4 rounded-2xl border border-slate-200/80 shadow-sm flex flex-col justify-between"
        >
          <div>
            <div className="flex items-center justify-between border-b border-slate-100 pb-2.5 mb-3">
              <div className="flex items-center gap-2">
                <div className="p-1.5 bg-emerald-50 text-emerald-600 rounded-lg">
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                  </svg>
                </div>
                <div>
                  <h4 className="text-[11px] font-bold text-slate-800 tracking-wide">Total Likes Distribution</h4>
                  <p className="text-[9px] text-slate-400">Suka per ID Marketing</p>
                </div>
              </div>
              <span className="bg-emerald-50 text-emerald-700 text-[8px] font-bold px-1.5 py-0.5 rounded-md border border-emerald-100/50">
                Likes
              </span>
            </div>

            <div className="h-[160px] w-full text-[9px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartDataGlobal} margin={{ top: 5, right: 5, left: -30, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                  <XAxis 
                    dataKey="brand" 
                    tickLine={false} 
                    axisLine={false} 
                    stroke="#64748b"
                    tick={{ fill: '#64748b', fontSize: 8, fontWeight: 600 }}
                  />
                  <YAxis 
                    tickLine={false} 
                    axisLine={false} 
                    stroke="#10b981"
                    tickFormatter={(val) => val >= 1e3 ? `${(val / 1e3).toFixed(0)}k` : val.toLocaleString()}
                    tick={{ fill: '#94a3b8', fontSize: 7 }}
                  />
                  <Tooltip
                    contentStyle={{ backgroundColor: '#fff', borderRadius: '10px', border: '1px solid #e2e8f0', fontSize: '9px', padding: '6px 10px' }}
                    formatter={(value) => [Number(value).toLocaleString("id-ID") + " Likes", "Total"]}
                  />
                  <Bar dataKey="total_likes" fill="#10b981" radius={[3, 3, 0, 0]} barSize={18}>
                    {chartDataGlobal.map((entry, index) => (
                      <Cell key={`cell-like-${index}`} fill="#10b981" fillOpacity={0.8 + (index % 2) * 0.2} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </motion.div>

        {/* CARD GRAPH 2: TOTAL SHARES ALL MK */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.1 }}
          className="bg-white p-4 rounded-2xl border border-slate-200/80 shadow-sm flex flex-col justify-between"
        >
          <div>
            <div className="flex items-center justify-between border-b border-slate-100 pb-2.5 mb-3">
              <div className="flex items-center gap-2">
                <div className="p-1.5 bg-indigo-50 text-indigo-600 rounded-lg">
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                  </svg>
                </div>
                <div>
                  <h4 className="text-[11px] font-bold text-slate-800 tracking-wide">Total Shares Distribution</h4>
                  <p className="text-[9px] text-slate-400">Sebaran per ID Marketing</p>
                </div>
              </div>
              <span className="bg-indigo-50 text-indigo-700 text-[8px] font-bold px-1.5 py-0.5 rounded-md border border-indigo-100/50">
                Shares
              </span>
            </div>

            <div className="h-[160px] w-full text-[9px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartDataGlobal} margin={{ top: 5, right: 5, left: -30, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                  <XAxis 
                    dataKey="brand" 
                    tickLine={false} 
                    axisLine={false} 
                    stroke="#64748b"
                    tick={{ fill: '#64748b', fontSize: 8, fontWeight: 600 }}
                  />
                  <YAxis 
                    tickLine={false} 
                    axisLine={false} 
                    stroke="#6366f1"
                    tickFormatter={(val) => val >= 1e3 ? `${(val / 1e3).toFixed(0)}k` : val.toLocaleString()}
                    tick={{ fill: '#94a3b8', fontSize: 7 }}
                  />
                  <Tooltip
                    contentStyle={{ backgroundColor: '#fff', borderRadius: '10px', border: '1px solid #e2e8f0', fontSize: '9px', padding: '6px 10px' }}
                    formatter={(value) => [Number(value).toLocaleString("id-ID") + " Shares", "Total"]}
                  />
                  <Bar dataKey="total_shares" fill="#6366f1" radius={[3, 3, 0, 0]} barSize={18}>
                    {chartDataGlobal.map((entry, index) => (
                      <Cell key={`cell-share-${index}`} fill="#6366f1" fillOpacity={0.8 + (index % 2) * 0.2} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </motion.div>

        {/* CARD 3: RANKING LIST TOP KOL PERFORMANCE */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.2 }}
          className="bg-white p-4 rounded-2xl border border-slate-200/80 shadow-sm flex flex-col justify-between"
        >
          <div className="w-full flex flex-col h-full">
            <div className="flex items-center justify-between border-b border-slate-100 pb-2.5 mb-2.5">
              <div className="flex items-center gap-2">
                <div className="p-1.5 bg-amber-50 text-amber-600 rounded-lg">
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                  </svg>
                </div>
                <div>
                  <h4 className="text-[11px] font-bold text-slate-800 tracking-wide">Top Creator Leaderboard</h4>
                  <p className="text-[9px] text-slate-400">KOL dengan akumulasi engangement tertinggi</p>
                </div>
              </div>
              <span className="bg-amber-50 text-amber-700 text-[8px] font-bold px-1.5 py-0.5 rounded-md border border-amber-100/50">
                Top Rank
              </span>
            </div>

            <div className="flex-1 flex flex-col justify-center divide-y divide-slate-50">
              {sortedKols.map((kol, idx) => (
                <div key={`leaderboard-kol-${idx}`} className="flex items-center justify-between py-1.5 first:pt-0 last:pb-0">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className={`text-[10px] font-bold w-4 h-4 flex items-center justify-center rounded-full ${
                      idx === 0 ? 'bg-amber-100 text-amber-700' : 
                      idx === 1 ? 'bg-slate-100 text-slate-600' : 
                      idx === 2 ? 'bg-orange-50 text-orange-700' : 'text-slate-400'
                    }`}>
                      {idx + 1}
                    </span>
                    <span className="text-[10px] font-semibold text-slate-700 truncate max-w-[100px]">
                      {kol.name}
                    </span>
                  </div>
                  
                  <div className="flex items-center gap-2.5 text-right shrink-0 font-mono text-[8.5px]">
                    <div className="flex flex-col">
                      <span className="text-slate-400 text-[7.5px] font-sans">Views</span>
                      <span className="font-bold text-slate-600">{kol.views >= 1e3 ? `${(kol.views / 1e3).toFixed(1)}k` : kol.views}</span>
                    </div>
                    <div className="flex flex-col">
                      <span className="text-slate-400 text-[7.5px] font-sans">Likes</span>
                      <span className="font-bold text-emerald-600">{kol.likes >= 1e3 ? `${(kol.likes / 1e3).toFixed(1)}k` : kol.likes}</span>
                    </div>
                    <div className="flex flex-col">
                      <span className="text-slate-400 text-[7.5px] font-sans">Shares</span>
                      <span className="font-bold text-indigo-500">{kol.shares >= 1e3 ? `${(kol.shares / 1e3).toFixed(1)}k` : kol.shares}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </motion.div>
      </>
    );
  })()}
</div>

    </PageWrapper>
  );
};